import { createTheme, ThemeProvider, type ThemeProviderProps } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { cn } from '@tempo-io/sui-class-utils';
import { KeyboardArrowDown } from '@tempo-io/sui-icons';
import type { ReactNode } from 'react';
import { useTheme } from '@/common/components/ThemeProvider';

declare module '@mui/material/styles' {
  interface Palette {
    activityCircle: string;
  }
  interface PaletteOptions {
    activityCircle?: string;
  }
  interface TypeBackground {
    activityCircle?: string;
  }
}

// SUI theme color mappings from OKLCH to hex/rgba
// --background: oklch(1.000 0.000 0.00) -> #FFFFFF
// --foreground: oklch(0.2103 0.0059 285.89) -> #18181B
// --card: oklch(1.000 0.000 0.00) -> #FFFFFF
// --muted: oklch(0.9674 0.0013 286.37) -> #F4F4F5
// --border: oklch(0.9197 0.004 286.32) -> #E4E4E7
// --primary: oklch(0.2103 0.0059 285.89) -> #18181B
// --destructive: oklch(0.5771 0.2152 27.33) -> #EF4444
// --radius: 0.625rem -> 10px

// Tailwind CSS color palette as CSS custom properties
export const tailwindColors = {
  white: '#ffffff',

  // Sky colors (used for secondary)
  sky50: '#f0f9ff',
  sky100: '#e0f2fe',
  sky200: '#bae6fd',
  sky300: '#7dd3fc',
  sky400: '#38bdf8',
  sky500: '#0ea5e9',
  sky600: '#0284c7',
  sky700: '#0369a1',
  sky800: '#075985',
  sky900: '#0c4a6e',

  // Zinc colors (used for text/foreground)
  zinc50: '#fafafa',
  zinc100: '#f4f4f5',
  zinc200: '#e4e4e7',
  zinc300: '#d4d4d8',
  zinc400: '#a1a1aa',
  zinc500: '#71717a',
  zinc600: '#52525b',
  zinc700: '#3f3f46',
  zinc800: '#27272a',
  zinc900: '#18181b',

  // Red colors (used for destructive/error)
  red50: '#fef2f2',
  red100: '#fee2e2',
  red200: '#fecaca',
  red300: '#fca5a5',
  red400: '#f87171',
  red500: '#ef4444',
  red600: '#dc2626',
  red700: '#b91c1c',
  red800: '#991b1b',
  red900: '#7f1d1d',
};

const createMuiTheme = (mode: 'light' | 'dark') =>
  createTheme({
    /**
     * Centralized MUI theme for Nexus frontend.
     *
     * Currently used by:
     * - Feedback & Idea Management (FIM) module
     * - Future: Swimlane component and other MUI-dependent features
     *
     * Customizations:
     * - Button: text-transform set to 'capitalize' (no uppercase)
     * - IconButton: borderRadius 8px, padding 6px
     * - Typography: System font stack with 14px base size
     * - Spacing: Base unit of 4px
     * - Shape: borderRadius 8px
     * - Palette: Custom text and primary colors for FIM design
     */
    components: {
      MuiAutocomplete: {
        defaultProps: {
          popupIcon: <KeyboardArrowDown />,
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
          inputRoot: {
            '&.MuiInputBase-sizeSmall': {
              paddingTop: '2.5px',
              paddingBottom: '2.5px',
              paddingLeft: '6px',
            },
          },
          tag: {
            borderRadius: 12,
          },
          option: {
            // Options are shown as "selected" (aria-selected=true) when already added as members,
            // but we don't want the default MUI gray selected highlight in this list.
            '&[aria-selected="true"]': {
              backgroundColor: 'var(--color-bg-surface-raised) !important',
            },
            '&[aria-selected="true"].Mui-focused': {
              backgroundColor: 'var(--color-bg-neutral-subtle-hovered) !important',
            },
            '&:hover': {
              backgroundColor: 'var(--color-bg-neutral-subtle-hovered) !important',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: '1px solid var(--color-border-neutral)', // --border
            backgroundColor: 'var(--color-bg-surface-raised)', // --card
            boxShadow: '0px 1px 3px 0px rgba(9, 9, 11, 0.10), 0px 1px 2px 0px rgba(9, 9, 11, 0.06)',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6, // --radius (0.625rem = 10px)
            borderColor: 'var(--color-border-neutral)', // --border / --input
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-text-neutral-primary)', // --ring
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-text-neutral-primary)', // --ring
            },
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: 6, // --radius
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            whiteSpace: 'nowrap',
            borderRadius: 6,
            fontSize: '0.875rem', // 14px to match SUI
            padding: '3.5px 12px',
          },
          contained: {
            backgroundColor: 'var(--color-bg-neutral-bold)', // --primary
            color: 'var(--color-text-neutral-inverted)', // --primary-foreground
            '&:hover': {
              backgroundColor: 'var(--color-bg-neutral-bold-hovered)',
            },
          },
          outlined: {
            borderColor: 'var(--color-border-neutral)', // --border
            color: 'var(--color-text-neutral-primary)', // --foreground
            '&:hover': {
              borderColor: 'var(--color-text-neutral-primary)',
              backgroundColor: 'var(--color-bg-neutral-subtle)', // --muted
            },
          },
          text: {
            color: 'var(--color-text-neutral-primary)', // --foreground
            '&:hover': {
              backgroundColor: 'var(--color-bg-neutral-subtle)', // --muted
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 6, // --radius
            padding: 6,
            '&:hover': {
              backgroundColor: 'var(--color-bg-neutral-subtle)', // --muted
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 4,
            color: mode === 'dark' ? tailwindColors.zinc500 : tailwindColors.zinc200, // --border
            '&.Mui-checked': {
              color: mode === 'dark' ? '#edeef0' : tailwindColors.zinc900, // --primary
            },
            '&.Mui-disabled': {
              color: mode === 'dark' ? tailwindColors.zinc700 : tailwindColors.zinc100,
            },
            '& svg': {
              borderRadius: 6, // smaller radius for checkboxes
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            background: 'var(--color-bg-neutral)', // --muted
            color: 'var(--color-text-neutral-primary)', // --foreground
            fontSize: '0.875rem',
            padding: '4px',
          },
          outlined: {
            backgroundColor: 'transparent',
            borderColor: 'var(--color-border-neutral)',
          },
          outlinedSecondary: {
            backgroundColor: tailwindColors.sky50,
            borderColor: tailwindColors.sky500,
            color: tailwindColors.sky700,
            '& .MuiChip-deleteIcon': {
              color: tailwindColors.sky700,
              '&:hover': {
                color: tailwindColors.sky800,
              },
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          IconComponent: ({ className, ...props }) => (
            <KeyboardArrowDown className={cn(className, '!top-auto')} {...props} />
          ),
        },
        styleOverrides: {
          root: {
            borderRadius: 6, // --radius
          },
          select: {
            '&.MuiOutlinedInput-input': {
              paddingTop: '6.5px',
              paddingBottom: '6.5px',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 6, // --radius
            backgroundColor: 'var(--color-bg-surface-raised)', // --card
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-bg-surface-raised)',
            color: 'var(--color-text-neutral-primary)',
          },
        },
      },
      MuiScopedCssBaseline: {
        styleOverrides: {
          root: {
            '&, & *': {
              WebkitFontSmoothing: 'auto',
              MozOsxFontSmoothing: 'auto',
            },
            backgroundColor: 'var(--color-bg-surface-base)',
            color: 'var(--color-text-neutral-primary)',
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          rowHeight: 35,
          columnHeaderHeight: 40,
        },
        styleOverrides: {
          root: {
            // Outer container styling to match Tempo SUI table
            borderColor: 'var(--color-border-neutral)',
            overflow: 'hidden',
            fontSize: '0.875rem', // 14px (text-sm)

            // Drive all of MUI's internal var(--DataGrid-rowBorderColor) references
            // (withLeftBorder, withRightBorder, row borders, etc.) to our zinc200 token.
            // This is the single source of truth for grid border color.
            '--DataGrid-rowBorderColor': 'var(--color-border-neutral)',
            '--DataGrid-columnHeaderBorderColor': 'var(--color-border-neutral)',
            backgroundColor: 'var(--color-bg-surface-base)',
            color: 'var(--color-text-neutral-primary)',

            '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight':
              {
                backgroundColor: 'var(--color-bg-surface-base)',
              },

            '& .MuiDataGrid-scrollShadow': {
              display: 'none',
            },

            '& .MuiDataGrid-columnHeader': {
              padding: '0 8px', // px-2 (matches Tempo SUI)
              color: 'var(--color-text-neutral-primary)',
              fontWeight: 500, // font-medium
              fontSize: '0.875rem', // 14px

              '&:focus, &:focus-within': {
                outline: 'none',
              },
              '&:focus-visible': {
                outline: '2px solid var(--color-text-neutral-primary)',
                outlineOffset: '-2px',
              },
            },

            // Reorderable column headers: grab while hovering / pressing.
            // MUI applies cursor: pointer to sortable headers; override it on the
            // draggable container (only present when column reorder is enabled).
            // Note: once the native HTML5 drag starts, the browser controls the
            // cursor (dropEffect: 'move') and CSS cursor is ignored — so a
            // `grabbing` cursor during the actual drag isn't achievable here.
            '& .MuiDataGrid-columnHeaderDraggableContainer[draggable="true"]': {
              cursor: 'grab',

              '&:active': {
                cursor: 'grabbing',
              },
            },

            // Vertical borders for headers (only when showColumnVerticalBorder={true})
            '& .MuiDataGrid-columnHeader--withRightBorder': {
              borderRightColor: 'var(--color-border-neutral)',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 500, // font-medium
              fontSize: '14px',
              color: 'var(--color-text-neutral-primary)',
              whiteSpace: 'nowrap', // whitespace-nowrap
            },

            // Sorted column header: highlight whole cell + title in blue,
            // and keep the sort arrow visible at all times in a white circle.
            '& .MuiDataGrid-columnHeader--sorted': {
              backgroundColor: 'var(--color-bg-accent-blue-subtle)',
              // boxShadow (not borderBottom) so it paints without changing box
              // height, which would otherwise get clipped by the fixed-height
              // header row. --color-border-accent-blue is a 20%-opacity tint,
              // too faint here, so use the solid --color-bg-accent-blue instead.
              boxShadow: 'inset 0 -2px 0 0 var(--color-bg-accent-blue)',

              '& .MuiDataGrid-columnHeaderTitle': {
                color: 'var(--color-text-accent-blue)',
              },

              // Some columns render custom header content (icon + text) via a
              // Tailwind-colored span instead of MuiDataGrid-columnHeaderTitle
              // (see HeaderWithIconFormatter), which needs its own override.
              '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainer *':
                {
                  color: 'var(--color-text-accent-blue) !important',
                },

              '& .MuiDataGrid-iconButtonContainer': {
                visibility: 'visible',
                width: 'auto',
              },

              // Keep the button's default (unsorted/hover) box size and padding
              // so the arrow sits the same distance from the title text as it
              // does when the column isn't sorted — only add the white circle.
              '& .MuiDataGrid-sortButton': {
                opacity: 1,
                backgroundColor: 'var(--color-bg-surface-base)',
                borderRadius: '50%',

                '& .MuiDataGrid-sortIcon': {
                  color: 'var(--color-icon-accent-blue)',
                },
              },
            },

            '& .MuiDataGrid-row': {
              '&:hover, &:hover .MuiDataGrid-cell': {
                backgroundColor: 'var(--color-bg-surface-base)',
                backgroundImage:
                  'linear-gradient(color-mix(in oklab, var(--color-bg-neutral-subtle) 50%, transparent), color-mix(in oklab, var(--color-bg-neutral-subtle) 50%, transparent))',
              },

              '&.Mui-selected, &.Mui-selected .MuiDataGrid-cell': {
                backgroundColor: 'var(--color-bg-surface-base)',
                backgroundImage:
                  'linear-gradient(var(--color-bg-neutral-subtle), var(--color-bg-neutral-subtle))',

                '&:hover, &:hover .MuiDataGrid-cell': {
                  backgroundImage:
                    'linear-gradient(var(--color-bg-neutral-subtle-hovered), var(--color-bg-neutral-subtle-hovered))',
                },
              },

              // Remove border from last row (matches Tempo SUI)
              '&:last-child': {
                borderBottom: 'none',
              },
            },

            '& .MuiDataGrid-cell': {
              padding: '8px', // p-2 (matches Tempo SUI)
              fontSize: '0.875rem', // 14px
              color: 'var(--color-text-neutral-primary)',
              whiteSpace: 'nowrap', // whitespace-nowrap
              display: 'flex',
              alignItems: 'center', // align-middle

              '&:focus, &:focus-within': {
                outline: 'none',
              },
              '&:focus-visible': {
                outline: '2px solid var(--color-text-neutral-primary)',
                outlineOffset: '-2px',
              },
            },

            // Vertical borders for cells (only when showCellVerticalBorder={true})
            '& .MuiDataGrid-cell--withRightBorder': {
              borderRightColor: 'var(--color-border-neutral)',
            },

            '& .MuiDataGrid-cellContent': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },

            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto',
            },

            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid var(--color-border-neutral)',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 34,
          },
          indicator: {
            height: 2,
            backgroundColor: 'var(--color-text-neutral-primary)',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px 0px 6px',
            gap: '8px',
            minHeight: 34,
            minWidth: 'fit-content',
            marginRight: '2rem',
            height: '18px',

            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '-0.2px',
            color: 'var(--color-text-neutral-primary)',
            textTransform: 'none',

            '&.Mui-selected': {
              color: 'var(--color-text-neutral-primary)',
              fontWeight: 500,
            },

            '&[aria-selected="false"]': {
              color: 'var(--color-text-neutral-secondary)',
            },

            '&.Mui-disabled': {
              opacity: 0.5,
              color: 'var(--color-text-neutral-primary)',
            },

            '&:hover': {
              color: 'var(--color-text-neutral-primary)',
              opacity: 0.8,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'var(--color-bg-neutral-bold)',
            color: 'var(--color-text-neutral-inverted)',
            fontSize: '0.75rem',
            borderRadius: 6,
          },
        },
      },
    },
    palette: {
      mode,
      text: {
        primary: mode === 'dark' ? '#edeef0' : tailwindColors.zinc900, // --foreground
        secondary: mode === 'dark' ? '#b0b4ba' : tailwindColors.zinc500, // --muted-foreground
      },
      primary: {
        main: mode === 'dark' ? '#edeef0' : tailwindColors.zinc900, // --primary
        contrastText: mode === 'dark' ? '#111113' : '#FFFFFF', // --primary-foreground
      },
      secondary: {
        main: tailwindColors.sky700, // --secondary
        contrastText: '#FFFFFF', // --secondary-foreground
      },
      error: {
        main: tailwindColors.red500, // --destructive
      },
      background: {
        default: mode === 'dark' ? '#111113' : '#FFFFFF', // --background
        paper: mode === 'dark' ? '#111113' : '#FFFFFF', // --card
        activityCircle: '#00BBC6',
      },
      divider: mode === 'dark' ? '#2e2e33' : tailwindColors.zinc200, // --border
      DataGrid: {
        bg: 'var(--color-bg-surface-base)',
        headerBg: 'var(--color-bg-surface-base)',
        pinnedBg: 'var(--color-bg-surface-base)',
      },
    },
    shape: {
      borderRadius: 6, // --radius (0.625rem = 10px)
    },
    spacing: 4,
    typography: {
      fontSize: 14,
      subtitle1: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        fontWeight: 600,
        textWrap: 'nowrap',
      },
      subtitle2: {
        fontSize: '1rem',
        lineHeight: 1.5,
        fontWeight: 600,
      },
      body1: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontSize: '0.875rem',
      },
      h5: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      fontFamily:
        'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    },
  });

export const muiTheme = createMuiTheme('light');
const muiDarkTheme = createMuiTheme('dark');

type MuiThemeProviderProps = Omit<ThemeProviderProps, 'theme'> & {
  theme?: ThemeProviderProps['theme'];
  children: ReactNode;
};

/**
 * Extensible MUI ThemeProvider wrapper for Nexus frontend.
 *
 * Provides the centralized MUI theme to all child components while allowing
 * theme overrides via props if needed.
 *
 * @example
 * // Basic usage (uses default muiTheme)
 * <MuiThemeProvider>
 *   <App />
 * </MuiThemeProvider>
 *
 * @example
 * // With custom theme override (rare, for special cases)
 * <MuiThemeProvider theme={customTheme}>
 *   <SpecialComponent />
 * </MuiThemeProvider>
 */
export const MuiThemeProvider = ({
  theme: themeProp,
  children,
  ...props
}: MuiThemeProviderProps) => {
  const { appliedTheme } = useTheme();
  const theme = themeProp ?? (appliedTheme === 'dark' ? muiDarkTheme : muiTheme);
  return (
    <ThemeProvider theme={theme} {...props}>
      {children}
    </ThemeProvider>
  );
};
